import React from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowLeft } from "lucide-react";
import "./App.css";

const Button = ({
  children,
  variant = "default",
  size = "default",
  className = "",
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background";

  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    outline: "border border-input hover:bg-accent hover:text-accent-foreground",
    ghost: "hover:bg-accent hover:text-accent-foreground",
  };

  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3 rounded-md",
    lg: "h-11 px-8 rounded-md",
  };

  const classes = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;

  return (
    <button className={classes} {...props}>
      {children}
    </button>
  );
};

function Terms() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="flex items-center justify-between px-6 py-4 md:px-12 border-b border-gray-200">
        <Link to="/" className="flex items-center space-x-2">
          <div className="bg-black rounded-full w-8 h-8 flex items-center justify-center">
            <Activity className="w-4 h-4 text-white" />
          </div>
          <span className="text-xl font-medium text-black">Sana</span>
        </Link>
        <Link to="/">
          <Button variant="ghost" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </Link>
      </nav>

      {/* Terms Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <h1 className="text-4xl md:text-5xl font-medium text-black mb-4">
          Terms and Conditions
        </h1>
        <p className="text-sm text-black opacity-60 mb-8">
          Last Updated:{" "}
          {new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>

        <div className="prose prose-lg max-w-none text-black">
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-lg leading-relaxed opacity-90 mb-6">
              Welcome to Sana ("we," "our," or "us"). These Terms and Conditions
              ("Terms") govern your access to and use of the Sana mobile
              application, website, and related services (collectively, the
              "Service"). By accessing or using our Service, you agree to be
              bound by these Terms. If you do not agree to these Terms, you must
              not use the Service.
            </p>
            <p className="text-lg leading-relaxed opacity-90">
              <strong className="text-black">IMPORTANT:</strong> Please read
              these Terms carefully, as they contain important information about
              your legal rights, remedies, and obligations. These Terms include
              limitations on our liability, disclaimers of warranties, and an
              agreement to resolve disputes through arbitration.
            </p>
          </section>

          {/* Medical Disclaimer */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              1. Medical Disclaimer and Not Medical Advice
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                <strong className="text-black">
                  THE SERVICE IS NOT INTENDED TO DIAGNOSE, TREAT, CURE, OR
                  PREVENT ANY DISEASE OR MEDICAL CONDITION.
                </strong>{" "}
                The information, protocols, recommendations, and content
                provided through the Service are for informational and
                educational purposes only and are not intended as, and shall not
                be understood or construed as, medical advice, diagnosis, or
                treatment.
              </p>
              <p>
                The biohacking protocols, health recommendations, and wellness
                information provided by Sana are based on general research and
                should not be considered as personalized medical advice.
                Individual results may vary, and the effectiveness of any
                protocol may differ from person to person.
              </p>
              <p>
                <strong className="text-black">
                  YOU MUST CONSULT WITH A QUALIFIED HEALTHCARE PROVIDER
                </strong>{" "}
                before starting any new health protocol, especially if you:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>
                  Have any pre-existing medical conditions or chronic diseases
                </li>
                <li>Are pregnant, nursing, or planning to become pregnant</li>
                <li>Are taking any prescription medications</li>
                <li>
                  Have a history of eating disorders, cardiovascular issues, or
                  other serious health conditions
                </li>
                <li>Are under the age of 18</li>
                <li>
                  Have any concerns about your health or the suitability of any
                  protocol
                </li>
              </ul>
              <p>
                Never disregard professional medical advice or delay in seeking
                it because of something you have read or learned through the
                Service. If you think you may have a medical emergency, call
                your doctor or emergency services immediately.
              </p>
            </div>
          </section>

          {/* User Responsibility */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              2. User Responsibility and Assumption of Risk
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>You acknowledge and agree that:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  You are solely responsible for your health and well-being, and
                  for any decisions you make regarding your health based on
                  information from the Service
                </li>
                <li>
                  You will consult with qualified healthcare professionals
                  before implementing any health protocols or making significant
                  changes to your diet, exercise, or lifestyle
                </li>
                <li>
                  You understand that biohacking protocols, including but not
                  limited to intermittent fasting, cold therapy, heat therapy,
                  and other interventions, may carry risks and are not suitable
                  for everyone
                </li>
                <li>
                  You will monitor your health and discontinue any protocol
                  immediately if you experience adverse effects
                </li>
                <li>
                  You will not use the Service if you have been advised by a
                  healthcare provider not to do so
                </li>
                <li>
                  You are responsible for ensuring that any health protocols you
                  follow are appropriate for your individual circumstances
                </li>
                <li>
                  You will not hold Sana responsible for any negative health
                  outcomes resulting from your use of the Service or
                  implementation of any protocols
                </li>
              </ul>
              <p>
                <strong className="text-black">
                  YOU EXPRESSLY ASSUME ALL RISKS
                </strong>{" "}
                associated with your use of the Service and any health protocols
                you choose to implement, including but not limited to physical
                injury, adverse health effects, and any other consequences that
                may arise from following protocols or recommendations provided
                through the Service.
              </p>
            </div>
          </section>

          {/* Eligibility */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              3. Eligibility and Age Restrictions
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                You must be at least 18 years old to use the Service. By using
                the Service, you represent and warrant that:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You are at least 18 years of age</li>
                <li>You have the legal capacity to enter into these Terms</li>
                <li>
                  You are not prohibited from using the Service under applicable
                  laws
                </li>
                <li>All information you provide is accurate and truthful</li>
              </ul>
              <p>
                If you are under 18, you may only use the Service with the
                consent and supervision of a parent or legal guardian who agrees
                to be bound by these Terms on your behalf.
              </p>
            </div>
          </section>

          {/* Service Access and Anonymous Sessions */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              4. Service Access and Anonymous Sessions
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                <strong className="text-black">
                  The Service uses anonymous sessions and does not require
                  account registration.
                </strong>{" "}
                You can access and use the Service without providing any
                personally identifiable information, such as your name, email
                address, or phone number.
              </p>
              <p>
                The Service operates using anonymous session identifiers that
                allow you to use the Service within a single session but cannot
                be used to identify you across different sessions or devices.
                These sessions are not linked to your identity.
              </p>
              <p>By using the Service, you acknowledge and agree that:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  You are responsible for maintaining the security of your
                  device and any local data stored on your device
                </li>
                <li>
                  You are responsible for all activities that occur during your
                  use of the Service within a session
                </li>
                <li>
                  Session data may be automatically cleared after periods of
                  inactivity
                </li>
                <li>
                  You understand that we cannot recover session data once it has
                  been cleared or deleted
                </li>
                <li>
                  You will not attempt to circumvent the anonymous session
                  system or attempt to link sessions to your identity
                </li>
              </ul>
              <p>
                We reserve the right to suspend or terminate access to the
                Service if we suspect any fraudulent, abusive, or illegal
                activity, or if you violate these Terms. Since we do not collect
                identifying information, such suspension or termination may be
                based on anonymous session identifiers or device
                characteristics.
              </p>
            </div>
          </section>

          {/* Health Data Privacy */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              5. Health Data and Privacy
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                <strong className="text-black">
                  The Service operates using anonymous sessions and does not
                  collect personally identifiable information.
                </strong>{" "}
                However, you may choose to input health-related information into
                the Service, including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Biometric data (heart rate variability, sleep data, activity
                  levels)
                </li>
                <li>Protocol adherence and tracking information</li>
                <li>Health goals and progress metrics</li>
                <li>Dietary and lifestyle information</li>
                <li>Other health-related data you choose to input</li>
              </ul>
              <p>
                <strong className="text-black">
                  Anonymous Data Collection:
                </strong>{" "}
                Health data you input is stored in anonymous sessions that
                cannot be linked to your identity. This data is not associated
                with personally identifiable information such as your name,
                email, or phone number. Much of your health data may also be
                stored locally on your device, giving you direct control over
                it.
              </p>
              <p>
                By using the Service, you consent to the collection, use, and
                storage of your health data as described in our Privacy Policy.
                Health data is considered sensitive information and will be
                handled in accordance with applicable privacy laws, including
                but not limited to the Health Insurance Portability and
                Accountability Act (HIPAA) where applicable, the General Data
                Protection Regulation (GDPR), and other relevant data protection
                regulations.
              </p>
              <p>
                You retain ownership of your health data. However, by using the
                Service, you grant us a license to use, process, and analyze
                your data (in anonymized and aggregated form only) to improve
                the Service, conduct research, and provide you with personalized
                recommendations within your session. We cannot and will not use
                this data to identify you personally.
              </p>
              <p>
                We implement industry-standard security measures to protect your
                health data, but no method of transmission or storage is 100%
                secure. You acknowledge that you provide your health data at
                your own risk.
              </p>
            </div>
          </section>

          {/* Acceptable Use */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              6. Acceptable Use Policy
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>You agree not to use the Service to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Violate any applicable laws, regulations, or third-party
                  rights
                </li>
                <li>Transmit any harmful, offensive, or illegal content</li>
                <li>
                  Impersonate any person or entity or misrepresent your
                  affiliation
                </li>
                <li>
                  Interfere with or disrupt the Service or servers connected to
                  the Service
                </li>
                <li>
                  Attempt to gain unauthorized access to the Service or related
                  systems
                </li>
                <li>
                  Use automated systems (bots, scrapers) to access the Service
                  without permission
                </li>
                <li>
                  Reverse engineer, decompile, or disassemble any part of the
                  Service
                </li>
                <li>
                  Remove or alter any copyright, trademark, or proprietary
                  notices
                </li>
                <li>
                  Use the Service to provide medical advice or diagnosis to
                  others
                </li>
                <li>Share false or misleading health information</li>
              </ul>
            </div>
          </section>

          {/* Intellectual Property */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              7. Intellectual Property Rights
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                The Service, including all content, features, functionality,
                protocols, designs, text, graphics, logos, and software, is
                owned by Sana or its licensors and is protected by copyright,
                trademark, patent, trade secret, and other intellectual property
                laws.
              </p>
              <p>
                You are granted a limited, non-exclusive, non-transferable,
                revocable license to access and use the Service for your
                personal, non-commercial use in accordance with these Terms.
                This license does not include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Any resale or commercial use of the Service</li>
                <li>
                  Collection and use of any product listings, descriptions, or
                  prices
                </li>
                <li>Derivative use of the Service or its contents</li>
                <li>
                  Use of data mining, robots, or similar data gathering and
                  extraction tools
                </li>
                <li>
                  Reproduction, distribution, or public performance of the
                  Service
                </li>
              </ul>
            </div>
          </section>

          {/* Disclaimers */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              8. Disclaimers of Warranties
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                <strong className="text-black">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
                  WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
                </strong>{" "}
                To the fullest extent permitted by applicable law, Sana
                disclaims all warranties, express or implied, including but not
                limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Implied warranties of merchantability, fitness for a
                  particular purpose, and non-infringement
                </li>
                <li>
                  Warranties that the Service will be uninterrupted, secure,
                  error-free, or free of viruses
                </li>
                <li>
                  Warranties regarding the accuracy, reliability, or
                  completeness of any information or content
                </li>
                <li>
                  Warranties that any health protocols will produce specific
                  results or benefits
                </li>
                <li>
                  Warranties regarding the safety or effectiveness of any
                  protocols or recommendations
                </li>
              </ul>
              <p>
                Some jurisdictions do not allow the exclusion of implied
                warranties, so some of the above exclusions may not apply to
                you.
              </p>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              9. Limitation of Liability
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                <strong className="text-black">
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
                  SHALL SANA, ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES,
                  AGENTS, OR LICENSORS BE LIABLE FOR:
                </strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Any indirect, incidental, special, consequential, or punitive
                  damages
                </li>
                <li>
                  Loss of profits, revenue, data, use, goodwill, or other
                  intangible losses
                </li>
                <li>
                  Damages resulting from your use of or inability to use the
                  Service
                </li>
                <li>
                  Damages resulting from any health protocols you implement
                  based on information from the Service
                </li>
                <li>
                  Any personal injury, illness, or health complications arising
                  from your use of the Service
                </li>
                <li>Any errors or omissions in the Service or content</li>
                <li>
                  Any unauthorized access to or use of our servers or your
                  personal information
                </li>
              </ul>
              <p>
                Our total liability to you for all claims arising from or
                related to the Service shall not exceed the amount you paid to
                us in the twelve (12) months preceding the claim, or $100,
                whichever is greater.
              </p>
              <p>
                Some jurisdictions do not allow the exclusion or limitation of
                incidental or consequential damages, so some of the above
                limitations may not apply to you.
              </p>
            </div>
          </section>

          {/* Indemnification */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              10. Indemnification
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                You agree to defend, indemnify, and hold harmless Sana, its
                affiliates, officers, directors, employees, agents, and
                licensors from and against any claims, liabilities, damages,
                losses, and expenses, including without limitation reasonable
                attorney's fees and costs, arising out of or in any way
                connected with:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your access to or use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>
                  Your violation of any third-party right, including any
                  intellectual property or privacy right
                </li>
                <li>
                  Your implementation of any health protocols or recommendations
                  from the Service
                </li>
                <li>
                  Any health-related decisions or actions you take based on
                  information from the Service
                </li>
                <li>
                  Any content you submit, post, or transmit through the Service
                </li>
              </ul>
            </div>
          </section>

          {/* Third-Party Services */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              11. Third-Party Services and Links
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                The Service may contain links to third-party websites, services,
                or resources that are not owned or controlled by Sana. We do not
                endorse or assume any responsibility for any third-party sites,
                information, materials, products, or services.
              </p>
              <p>
                If you access any third-party website, service, or content from
                the Service, you do so at your own risk and subject to the terms
                and conditions of such third parties. We are not responsible for
                any loss or damage that may arise from your use of any
                third-party services.
              </p>
            </div>
          </section>

          {/* Termination */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              12. Termination
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                We may terminate or suspend your access to the Service
                immediately, without prior notice or liability, for any reason,
                including if you breach these Terms. Since the Service uses
                anonymous sessions, such termination may be based on anonymous
                session identifiers or device characteristics.
              </p>
              <p>
                Upon termination, your right to use the Service will immediately
                cease. All provisions of these Terms that by their nature should
                survive termination shall survive, including ownership
                provisions, warranty disclaimers, indemnity, and limitations of
                liability.
              </p>
              <p>
                You may stop using the Service at any time. You can clear your
                session data and local data stored on your device through the
                Service settings or by uninstalling the Service. Since we do not
                collect personally identifiable information, we cannot identify
                and delete specific data associated with you across sessions.
                However, anonymous session data is automatically deleted after
                periods of inactivity, as described in our Privacy Policy.
              </p>
            </div>
          </section>

          {/* Changes to Terms */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              13. Changes to Terms
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                We reserve the right, at our sole discretion, to modify or
                replace these Terms at any time. If a revision is material, we
                will provide at least 30 days' notice prior to any new terms
                taking effect.
              </p>
              <p>
                What constitutes a material change will be determined at our
                sole discretion. By continuing to access or use the Service
                after any revisions become effective, you agree to be bound by
                the revised Terms. If you do not agree to the new Terms, you
                must stop using the Service.
              </p>
            </div>
          </section>

          {/* Governing Law */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              14. Governing Law and Dispute Resolution
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                These Terms shall be governed by and construed in accordance
                with the laws of the jurisdiction in which Sana operates,
                without regard to its conflict of law provisions.
              </p>
              <p>
                <strong className="text-black">ARBITRATION AGREEMENT:</strong>{" "}
                Any dispute, controversy, or claim arising out of or relating to
                these Terms or the Service shall be settled by binding
                arbitration in accordance with the rules of a recognized
                arbitration organization. The arbitration shall be conducted in
                a mutually agreed location, and judgment on the arbitration
                award may be entered in any court having jurisdiction.
              </p>
              <p className="text-sm opacity-70 italic mt-2">
                Note: Please consult with legal counsel to specify your
                jurisdiction, arbitration organization, and location based on
                your business location and applicable laws.
              </p>
              <p>
                You agree that any dispute resolution proceedings will be
                conducted only on an individual basis and not in a class,
                consolidated, or representative action. If for any reason a
                claim proceeds in court rather than in arbitration, you waive
                any right to a jury trial.
              </p>
            </div>
          </section>

          {/* Severability */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              15. Severability
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                If any provision of these Terms is found to be unenforceable or
                invalid, that provision shall be limited or eliminated to the
                minimum extent necessary so that these Terms shall otherwise
                remain in full force and effect and enforceable.
              </p>
            </div>
          </section>

          {/* Entire Agreement */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              16. Entire Agreement
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                These Terms, together with our Privacy Policy and any other
                legal notices published by us on the Service, shall constitute
                the entire agreement between you and Sana concerning the Service
                and supersede all prior or contemporaneous communications and
                proposals, whether oral or written, between you and Sana.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-12">
            <h2 className="text-3xl font-medium text-black mb-4 mt-8">
              17. Contact Information
            </h2>
            <div className="space-y-4 text-black opacity-90 leading-relaxed">
              <p>
                If you have any questions about these Terms, please contact us
                at:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg mt-4">
                <p className="font-medium mb-2">Sana Support</p>
                <p>Email: support@sana.app</p>
                <p className="mt-2 text-sm opacity-70 italic">
                  Note: Add your physical business address here if required by
                  applicable law (e.g., GDPR, CCPA).
                </p>
              </div>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="mb-12 mt-16 p-8 bg-gray-50 rounded-lg border-2 border-black">
            <p className="text-lg font-medium text-black mb-4">
              Acknowledgment
            </p>
            <p className="text-black opacity-90 leading-relaxed">
              BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE
              TERMS AND CONDITIONS, UNDERSTAND THEM, AND AGREE TO BE BOUND BY
              THEM. YOU FURTHER ACKNOWLEDGE THAT THESE TERMS CONSTITUTE A
              LEGALLY BINDING AGREEMENT BETWEEN YOU AND SANA, AND THAT YOUR USE
              OF THE SERVICE IS SUBJECT TO ALL APPLICABLE LAWS AND REGULATIONS.
            </p>
          </section>
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="bg-black rounded-full w-8 h-8 flex items-center justify-center">
                <Activity className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-medium text-black">Sana</span>
            </div>
            <div className="flex items-center space-x-6 text-sm text-black opacity-70">
              <Link
                to="/privacy"
                className="hover:opacity-100 transition-opacity"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                className="hover:opacity-100 transition-opacity"
              >
                Terms
              </Link>
              <Link
                to="/support"
                className="hover:opacity-100 transition-opacity"
              >
                Support
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-200 text-center">
            <p className="text-sm text-black opacity-50">
              © 2024 Sana. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default Terms;
